import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { AdditionFact } from '../data/curriculum';

interface NumberLineProps {
  rangeStart: number;
  rangeEnd: number;
  fact: AdditionFact;
  animate: boolean;
  animationDelay?: number;
  loop?: boolean;
}

const NumberLine: React.FC<NumberLineProps> = ({
  rangeStart,
  rangeEnd,
  fact,
  animate,
  animationDelay = 0,
  loop = false,
}) => {
  const [cycle, setCycle] = useState(0);

  // Only use animationDelay on first cycle
  const d = cycle === 0 ? animationDelay : 0;
  const animEndTime = d + 0.95; // when all animations finish

  useEffect(() => {
    if (!loop || !animate) return;
    const timer = setTimeout(() => {
      setCycle((c) => c + 1);
    }, (animEndTime + 0.8) * 1000); // animation end + pause before replaying
    return () => clearTimeout(timer);
  }, [loop, animate, cycle, animEndTime]);

  const numPoints = rangeEnd - rangeStart + 1;
  const spacing = 60;
  const paddingX = 30;
  const svgWidth = paddingX * 2 + (numPoints - 1) * spacing;
  const lineY = 90;
  const svgHeight = 130;
  const dotRadius = 7;

  const getX = (num: number) => paddingX + (num - rangeStart) * spacing;

  const isZeroAddition = fact.addend2 === 0;

  const fromX = getX(fact.addend1);
  const toX = getX(fact.sum);
  const midX = (fromX + toX) / 2;
  const arcHeight = 35 + fact.addend2 * 5;
  const arcPeakY = lineY - arcHeight;

  const arcPath = `M ${fromX} ${lineY - dotRadius} Q ${midX} ${arcPeakY} ${toX} ${lineY - dotRadius}`;

  return (
    <div className="w-full flex justify-center my-4">
      <svg
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        className="w-full"
        style={{ maxWidth: svgWidth }}
      >
        {/* Base line */}
        <line
          x1={paddingX}
          y1={lineY}
          x2={svgWidth - paddingX}
          y2={lineY}
          stroke="#CBD5E1"
          strokeWidth="2.5"
          strokeLinecap="round"
        />

        {/* Tick marks and number labels */}
        {Array.from({ length: numPoints }, (_, i) => {
          const num = rangeStart + i;
          const x = getX(num);
          const isFrom = num === fact.addend1;
          const isTo = num === fact.sum;
          const isActive = isFrom || isTo;

          return (
            <g key={num}>
              <line
                x1={x}
                y1={lineY - 6}
                x2={x}
                y2={lineY + 6}
                stroke={isActive ? '#6366F1' : '#94A3B8'}
                strokeWidth="2"
              />
              <text
                x={x}
                y={lineY + 24}
                textAnchor="middle"
                fontSize="14"
                fontWeight={isActive ? 700 : 400}
                fill={isActive ? '#4F46E5' : '#64748B'}
              >
                {num}
              </text>
            </g>
          );
        })}

        {/* Animated elements — keyed by cycle to replay on loop */}
        <g key={cycle}>
          {animate && isZeroAddition && (
            <>
              {/* +0: Dot appears at position */}
              <motion.circle
                cx={fromX}
                cy={lineY}
                r={dotRadius}
                fill="#6366F1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.25, delay: d }}
              />

              {/* +0 label above */}
              <motion.text
                x={fromX}
                y={lineY - 22}
                textAnchor="middle"
                fontSize="13"
                fontWeight={700}
                fill="#6366F1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.25, delay: d + 0.3 }}
              >
                +0
              </motion.text>

              {/* Dot transitions to green — answer is same position */}
              <motion.circle
                cx={fromX}
                cy={lineY}
                r={dotRadius}
                fill="#10B981"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: d + 0.6 }}
              />
            </>
          )}

          {animate && !isZeroAddition && (
            <>
              {/* Starting position dot */}
              <motion.circle
                cx={fromX}
                cy={lineY}
                r={dotRadius}
                fill="#6366F1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.25, delay: d }}
              />

              {/* Hop arc */}
              <motion.path
                d={arcPath}
                fill="none"
                stroke="#6366F1"
                strokeWidth="2.5"
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.5, delay: d + 0.2, ease: 'easeInOut' }}
              />

              {/* +N label above arc */}
              <motion.text
                x={midX}
                y={arcPeakY - 6}
                textAnchor="middle"
                fontSize="13"
                fontWeight={700}
                fill="#6366F1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.25, delay: d + 0.35 }}
              >
                +{fact.addend2}
              </motion.text>

              {/* Landing position dot */}
              <motion.circle
                cx={toX}
                cy={lineY}
                r={dotRadius}
                fill="#10B981"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.25, delay: d + 0.7 }}
              />
            </>
          )}
        </g>
      </svg>
    </div>
  );
};

export default NumberLine;
